import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import { GreenText } from 'components'
import { VehicleInvoicingDto, vehicleInvoicingValidationSchema } from 'dtos'
import { Formik } from 'formik'

interface InvoicingDetailsAddEditDialogProps {
  initialValues: VehicleInvoicingDto
  isLoading: boolean
  onClose: () => void
  onSave: (values: any) => void
  open: boolean
}

export default function InvoicingDetailsAddEditDialog({ initialValues, isLoading, onClose, onSave, open }: InvoicingDetailsAddEditDialogProps) {
  return (
    <Dialog fullWidth maxWidth='sm' onClose={onClose} open={open}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={values => onSave(values)}
        validateOnBlur
        validateOnChange
        validationSchema={vehicleInvoicingValidationSchema}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <DialogTitle>Edit Invoice Information</DialogTitle>

              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingImportCharge && errors.invoicingImportCharge)}
                    fullWidth
                    helperText={touched.invoicingImportCharge && errors.invoicingImportCharge}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Import Charge'
                    name='invoicingImportCharge'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingImportCharge}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingDuty && errors.invoicingDuty)}
                    fullWidth
                    helperText={touched.invoicingDuty && errors.invoicingDuty}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Duty'
                    name='invoicingDuty'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingDuty}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingInboundTransportation && errors.invoicingInboundTransportation)}
                    fullWidth
                    helperText={touched.invoicingInboundTransportation && errors.invoicingInboundTransportation}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Inbound Transportation'
                    name='invoicingInboundTransportation'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingInboundTransportation}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingOutboundTransportation && errors.invoicingOutboundTransportation)}
                    fullWidth
                    helperText={touched.invoicingOutboundTransportation && errors.invoicingOutboundTransportation}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Outbound Transportation'
                    name='invoicingOutboundTransportation'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingOutboundTransportation}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingClusterConversion && errors.invoicingClusterConversion)}
                    fullWidth
                    helperText={touched.invoicingClusterConversion && errors.invoicingClusterConversion}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Cluster Conversion'
                    name='invoicingClusterConversion'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingClusterConversion}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingClusterProgramming && errors.invoicingClusterProgramming)}
                    fullWidth
                    helperText={touched.invoicingClusterProgramming && errors.invoicingClusterProgramming}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Cluster Programming'
                    name='invoicingClusterProgramming'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingClusterProgramming}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingClusterReplacement && errors.invoicingClusterReplacement)}
                    fullWidth
                    helperText={touched.invoicingClusterReplacement && errors.invoicingClusterReplacement}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Cluster Replacement'
                    name='invoicingClusterReplacement'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingClusterReplacement}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingTitleFees && errors.invoicingTitleFees)}
                    fullWidth
                    helperText={touched.invoicingTitleFees && errors.invoicingTitleFees}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Title Fees'
                    name='invoicingTitleFees'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingTitleFees}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingRecallExpenses && errors.invoicingRecallExpenses)}
                    fullWidth
                    helperText={touched.invoicingRecallExpenses && errors.invoicingRecallExpenses}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Recall Expenses'
                    name='invoicingRecallExpenses'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingRecallExpenses}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingFedEx && errors.invoicingFedEx)}
                    fullWidth
                    helperText={touched.invoicingFedEx && errors.invoicingFedEx}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='FedEx'
                    name='invoicingFedEx'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingFedEx}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingFedExAPI && errors.invoicingFedExAPI)}
                    fullWidth
                    helperText={touched.invoicingFedExAPI && errors.invoicingFedExAPI}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='FedEx API'
                    name='invoicingFedExAPI'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingFedExAPI}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingReplacementLabel && errors.invoicingReplacementLabel)}
                    fullWidth
                    helperText={touched.invoicingReplacementLabel && errors.invoicingReplacementLabel}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Replacement Label'
                    name='invoicingReplacementLabel'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingReplacementLabel}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingRepairs && errors.invoicingRepairs)}
                    fullWidth
                    helperText={touched.invoicingRepairs && errors.invoicingRepairs}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Repairs'
                    name='invoicingRepairs'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingRepairs}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingVehicleCleanUp && errors.invoicingVehicleCleanUp)}
                    fullWidth
                    helperText={touched.invoicingVehicleCleanUp && errors.invoicingVehicleCleanUp}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Vehicle Clean Up'
                    name='invoicingVehicleCleanUp'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingVehicleCleanUp}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingMiscellaneous && errors.invoicingMiscellaneous)}
                    fullWidth
                    helperText={touched.invoicingMiscellaneous && errors.invoicingMiscellaneous}
                    InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                    label='Miscellaneous'
                    name='invoicingMiscellaneous'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onWheel={e => e.target instanceof HTMLElement && e.target.blur()}
                    type='number'
                    value={values.invoicingMiscellaneous}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    disabled={isLoading}
                    error={Boolean(touched.invoicingNotes && errors.invoicingNotes)}
                    fullWidth
                    helperText={touched.invoicingNotes && errors.invoicingNotes}
                    label='Invoice Notes'
                    maxRows={4}
                    minRows={4}
                    multiline
                    name='invoicingNotes'
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.invoicingNotes || ''}
                  />
                </Grid>

                <Grid item xs={12}>
                  <GreenText variant='outlined'>
                    {`Values in the Edit Invoicing Information dialog may be modified in three ways:

   - VTS2 will automatically update the amount for an individual charge type when a new transaction of the same charge type is added to the transactions table.
   - The values in this dialog are modified by users via the Invoicing feature.
   - The values in this dialog are directly edited by users.

This dialog allows users to manually adjust the amount for each charge type.
As vehicle-related activities are completed, transactions are automatically
added to the transactions table. When a transaction is added, its amount 
updates the corresponding field in this dialog (either increasing or decreasing 
the existing value) rather than recalculating the total from all related 
transactions. This approach provides the flexibility needed to accommodate the 
diverse agreements and contracts BWI maintains with vendors, exporters, and 
buyers.

When users in the Invoicing feature select this vehicle and modify the various 
charges or Invoice Notes within the Invoicing Services section, changes made 
under the Invoicing feature overwrite the values in this dialog.`}
                  </GreenText>
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <Button color='inherit' disabled={isLoading} onClick={onClose} variant='text'>
                CANCEL
              </Button>

              <LoadingButton loading={isLoading} type='submit' onClick={() => console.log(errors)} variant='contained'>
                SAVE
              </LoadingButton>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  )
}
